import type { EntityFilterSchema, FilterField } from './types.js';

/** Half-open date window. Either side optional; values are YYYY-MM-DD strings. */
export interface FilterDateRange {
  after?: string;
  before?: string;
}

/**
 * Inclusive numeric window. Either bound optional; values stay digit-only
 * strings in state/URL (the API layer coerces `<key>_min`/`<key>_max` params
 * to numbers).
 */
export interface FilterNumberRange {
  min?: string;
  max?: string;
}

export type FilterFieldValue = string[] | FilterDateRange | FilterNumberRange;

/** Either range shape, viewed structurally, for schema-independent helpers. */
type AnyRange = Partial<Record<'after' | 'before' | 'min' | 'max', string>>;

/**
 * Canonical registry-keyed filter state emitted by the web filter bar and
 * persisted in saved views / URL search params. `q` is the plain-text Search
 * chip; `fields` holds the non-text registry fields keyed by FilterField.key.
 * Entity fields are single-select but stored as one-element arrays so every
 * non-date value has the same shape.
 */
export interface FilterState {
  q: string;
  fields: Record<string, FilterFieldValue>;
}

export const EMPTY_FILTER_STATE: FilterState = Object.freeze({
  q: '',
  fields: Object.freeze({}) as Record<string, FilterFieldValue>,
});

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const DIGITS_RE = /^\d+$/;

/**
 * Shape *and* calendar validity. The View definition contract validates dates with
 * `z.string().date()`, so a shape-only check here would let `2026-02-30` survive URL
 * parsing and an Ask response, then throw when the View is saved.
 */
function isDateString(value: unknown): value is string {
  if (typeof value !== 'string' || !DATE_RE.test(value)) return false;
  // Round-trip through the ISO parser rather than Date.UTC(year, ...), whose legacy
  // two-digit-year shorthand maps 0..99 into the 1900s and would reject `0099-03-15`.
  // Overflow still fails the comparison: `2026-02-30` re-serialises as `2026-03-02`.
  const utc = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(utc.getTime()) && utc.toISOString().startsWith(value);
}

/**
 * Canonical digit string for a number-range bound, or undefined when invalid.
 * Accepts non-negative integer numbers too — URL search parsers (e.g. TanStack
 * Router) JSON-parse values, so `?sqft_min=30000` arrives as a number.
 */
function parseNumberBound(value: unknown): string | undefined {
  if (typeof value === 'string' && DIGITS_RE.test(value)) return value;
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0) return String(value);
  return undefined;
}

export function hasActiveFilters(state: FilterState): boolean {
  if (state.q.trim().length > 0) return true;
  for (const value of Object.values(state.fields)) {
    if (Array.isArray(value)) {
      if (value.length > 0) return true;
    } else {
      const range = value as AnyRange;
      if (range.after || range.before || range.min || range.max) return true;
    }
  }
  return false;
}

/** Fresh range copy in deterministic {after, before, min, max} order; only set sides kept. */
function canonicalRange(value: AnyRange): AnyRange {
  const range: AnyRange = {};
  if (value.after) range.after = value.after;
  if (value.before) range.before = value.before;
  if (value.min) range.min = value.min;
  if (value.max) range.max = value.max;
  return range;
}

function rangeIsEmpty(range: AnyRange): boolean {
  return !range.after && !range.before && !range.min && !range.max;
}

/**
 * Canonical copy of a FilterState: trimmed q, empty arrays and empty ranges
 * (date or number) dropped, fresh objects throughout (never aliases the
 * input). Field insertion order is preserved.
 */
export function canonicalFilterState(state: FilterState): FilterState {
  const fields: Record<string, FilterFieldValue> = {};
  for (const [key, value] of Object.entries(state.fields)) {
    if (value == null) continue;
    if (Array.isArray(value)) {
      if (value.length > 0) fields[key] = [...value];
      continue;
    }
    const range = canonicalRange(value as AnyRange);
    if (!rangeIsEmpty(range)) fields[key] = range as FilterFieldValue;
  }
  return { q: state.q.trim(), fields };
}

function canonicalize(state: FilterState) {
  const fields: Record<string, unknown> = {};
  for (const key of Object.keys(state.fields).sort()) {
    const value = state.fields[key];
    if (value == null) continue;
    if (Array.isArray(value)) {
      // Skip empty arrays — they are equivalent to no filter
      if (value.length === 0) continue;
      fields[key] = [...value].sort();
    } else {
      const range = canonicalRange(value as AnyRange);
      // Skip empty ranges — they are equivalent to no filter
      if (rangeIsEmpty(range)) continue;
      // Rebuilt with deterministic property order so JSON.stringify is stable
      fields[key] = range;
    }
  }
  return { q: state.q.trim(), fields };
}

export function filterStatesEqual(a: FilterState, b: FilterState): boolean {
  return JSON.stringify(canonicalize(a)) === JSON.stringify(canonicalize(b));
}

/**
 * Flatten a FilterState into API query-param entries for the entity's list
 * endpoint: repeatable keys for multi values, `<key>_after`/`<key>_before`
 * for date ranges, `<key>_min`/`<key>_max` for number ranges, `q` for text
 * search. Unknown keys are skipped.
 */
export function filterStateToParams(
  schema: EntityFilterSchema,
  state: FilterState,
): [string, string][] {
  const entries: [string, string][] = [];
  const q = state.q.trim();
  if (q) entries.push(['q', q]);
  for (const field of schema.fields) {
    if (field.type === 'text') continue;
    const value = state.fields[field.key];
    if (value == null) continue;
    if (field.type === 'date-range') {
      if (Array.isArray(value)) continue;
      const range = value as FilterDateRange;
      if (range.after) entries.push([`${field.key}_after`, range.after]);
      if (range.before) entries.push([`${field.key}_before`, range.before]);
      continue;
    }
    if (field.type === 'number-range') {
      if (Array.isArray(value)) continue;
      const range = value as FilterNumberRange;
      if (range.min) entries.push([`${field.key}_min`, range.min]);
      if (range.max) entries.push([`${field.key}_max`, range.max]);
      continue;
    }
    if (!Array.isArray(value)) continue;
    for (const v of value) entries.push([field.key, v]);
  }
  return entries;
}

function isSingleValued(field: FilterField): boolean {
  return field.type === 'entity' || !('multi' in field) || !field.multi;
}

/**
 * Serialize a FilterState into a flat search-params object (one key per
 * field, arrays for multi values, `<key>_after`/`<key>_before` for dates,
 * `<key>_min`/`<key>_max` for number ranges) for URL state. Inverse of
 * filterStateFromSearch for valid states.
 */
export function filterStateToSearch(
  schema: EntityFilterSchema,
  state: FilterState,
): Record<string, string | string[]> {
  const search: Record<string, string | string[]> = {};
  const q = state.q.trim();
  if (q) search.q = q;
  for (const field of schema.fields) {
    if (field.type === 'text') continue;
    const value = state.fields[field.key];
    if (value == null) continue;
    if (field.type === 'date-range') {
      if (Array.isArray(value)) continue;
      const range = value as FilterDateRange;
      if (range.after) search[`${field.key}_after`] = range.after;
      if (range.before) search[`${field.key}_before`] = range.before;
      continue;
    }
    if (field.type === 'number-range') {
      if (Array.isArray(value)) continue;
      const range = value as FilterNumberRange;
      if (range.min) search[`${field.key}_min`] = range.min;
      if (range.max) search[`${field.key}_max`] = range.max;
      continue;
    }
    if (!Array.isArray(value) || value.length === 0) continue;
    if (isSingleValued(field)) {
      const [first] = value;
      if (first == null) continue;
      search[field.key] = first;
    } else {
      search[field.key] = [...value];
    }
  }
  return search;
}

function parseStringList(raw: unknown): { values: string[]; hadInvalid: boolean } {
  const list = Array.isArray(raw) ? raw : [raw];
  const values = list.filter((v): v is string => typeof v === 'string' && v.length > 0);
  return { values, hadInvalid: values.length !== list.length };
}

function parseEnumList(
  field: Extract<FilterField, { type: 'enum' }>,
  raw: unknown,
): { values: string[]; hadInvalid: boolean } {
  const allowed = new Set(field.values.map((v) => v.value));
  const { values, hadInvalid } = parseStringList(raw);
  const valid = values.filter((v) => allowed.has(v));
  return { values: valid, hadInvalid: hadInvalid || valid.length !== values.length };
}

/**
 * Parse an untrusted flat search-params object (URL state) into a normalized
 * FilterState. Returns the keys that were dropped or partially invalid so the
 * caller can warn; never throws.
 */
export function filterStateFromSearch(
  schema: EntityFilterSchema,
  search: Record<string, unknown>,
): { state: FilterState; dropped: string[] } {
  const state: FilterState = { q: '', fields: {} };
  const dropped: string[] = [];
  const consumed = new Set<string>();

  for (const field of schema.fields) {
    if (field.type === 'text') {
      consumed.add(field.key);
      const raw = search[field.key];
      if (raw == null) continue;
      if (typeof raw === 'string') state.q = raw;
      else dropped.push(field.key);
      continue;
    }
    if (field.type === 'date-range') {
      const range: FilterDateRange = {};
      for (const side of ['after', 'before'] as const) {
        const key = `${field.key}_${side}`;
        consumed.add(key);
        const raw = search[key];
        if (raw == null) continue;
        if (isDateString(raw)) range[side] = raw;
        else dropped.push(key);
      }
      if (range.after || range.before) state.fields[field.key] = range;
      continue;
    }
    if (field.type === 'number-range') {
      const range: FilterNumberRange = {};
      for (const side of ['min', 'max'] as const) {
        const key = `${field.key}_${side}`;
        consumed.add(key);
        const raw = search[key];
        if (raw == null) continue;
        const bound = parseNumberBound(raw);
        if (bound != null) range[side] = bound;
        else dropped.push(key);
      }
      if (range.min || range.max) state.fields[field.key] = range;
      continue;
    }
    consumed.add(field.key);
    const raw = search[field.key];
    if (raw == null) continue;
    const { values, hadInvalid } =
      field.type === 'enum' ? parseEnumList(field, raw) : parseStringList(raw);
    if (hadInvalid) dropped.push(field.key);
    if (values.length === 0) continue;
    state.fields[field.key] = isSingleValued(field) ? values.slice(0, 1) : values;
  }

  for (const key of Object.keys(search)) {
    if (!consumed.has(key) && search[key] != null) dropped.push(key);
  }
  return { state, dropped };
}

/**
 * Normalize an untrusted stored payload (saved view) shaped like
 * `{ q, fields }` into a valid FilterState, silently dropping anything
 * invalid. Legacy or garbage payloads normalize to the empty state.
 */
export function normalizeFilterState(schema: EntityFilterSchema, raw: unknown): FilterState {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return EMPTY_FILTER_STATE;
  const candidate = raw as { q?: unknown; fields?: unknown };
  const fieldsRaw =
    candidate.fields && typeof candidate.fields === 'object' && !Array.isArray(candidate.fields)
      ? (candidate.fields as Record<string, unknown>)
      : {};

  const state: FilterState = {
    q: typeof candidate.q === 'string' ? candidate.q : '',
    fields: {},
  };

  for (const field of schema.fields) {
    if (field.type === 'text') continue;
    const fieldRaw = fieldsRaw[field.key];
    if (fieldRaw == null) continue;
    if (field.type === 'date-range') {
      if (!fieldRaw || typeof fieldRaw !== 'object' || Array.isArray(fieldRaw)) continue;
      const rawRange = fieldRaw as Record<string, unknown>;
      const range: FilterDateRange = {};
      if (isDateString(rawRange.after)) range.after = rawRange.after;
      if (isDateString(rawRange.before)) range.before = rawRange.before;
      if (range.after || range.before) state.fields[field.key] = range;
      continue;
    }
    if (field.type === 'number-range') {
      if (!fieldRaw || typeof fieldRaw !== 'object' || Array.isArray(fieldRaw)) continue;
      const rawRange = fieldRaw as Record<string, unknown>;
      const range: FilterNumberRange = {};
      const min = parseNumberBound(rawRange.min);
      const max = parseNumberBound(rawRange.max);
      if (min != null) range.min = min;
      if (max != null) range.max = max;
      if (range.min || range.max) state.fields[field.key] = range;
      continue;
    }
    const { values } =
      field.type === 'enum' ? parseEnumList(field, fieldRaw) : parseStringList(fieldRaw);
    if (values.length === 0) continue;
    state.fields[field.key] = isSingleValued(field) ? values.slice(0, 1) : values;
  }
  return state;
}
